import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";

import {
    getVenue,
    getVenueBoard,
    getVenuesNear,
    statusOfError,
    VenueBoardPublic,
    VenuePublic,
} from "../../services/venueService";
import {
    CentrePoint,
    isUsableCentrePoint,
} from "../../pages/venue/venueHistory";
import {
    boardKey,
    clearVenueStore,
    load,
    nearKey,
    useVenueStoreVersion,
    venueKey,
} from "./venueStore";

/**
 * Resolving a venue and its board, shared by the two SCORZA-739 surfaces.
 *
 * Two ways in, and they are not interchangeable:
 *
 *  - **By id** — the result page. `match.venueRef.venueId` is the venue the server actually filed
 *    the entry under, so the ribbon and the board behind it cannot disagree.
 *  - **By centre point** — `VenuePage` deep links, and only those. Proximity picks the nearest
 *    centroid within 5 km, whereas the server matched a pitch footprint by IoU; they can and do
 *    differ at a multi-pitch complex or two grounds either side of a hedge. It is the right
 *    answer for "what is near this coordinate" and the wrong one for "which board is this match
 *    on", which is why the ribbon path no longer uses it.
 *
 * Caching, sharing and invalidation all live in `venueStore`; this hook is the React binding and
 * the one place a cache key is paired with the call that fills it. The store deliberately knows
 * nothing about `venueService` — see its header for why.
 */

const loadVenue = (venueId: string) =>
    load(venueKey(venueId), () => getVenue(venueId));

const loadBoard = (venueId: string) =>
    load(boardKey(venueId), () => getVenueBoard(venueId));

const loadVenuesNear = (latitude: number, longitude: number) =>
    load(nearKey(latitude, longitude), () =>
        getVenuesNear(latitude, longitude)
    );

/** `/venues/near` sorts by distance, but do not rely on it. */
export function nearestVenue(venues: VenuePublic[]): VenuePublic | null {
    if (!venues.length) {
        return null;
    }
    return venues.reduce((closest, venue) =>
        (venue.distanceM ?? Infinity) < (closest.distanceM ?? Infinity)
            ? venue
            : closest
    );
}

/** How the community half resolved. The personal half renders in every one of these states. */
export type CommunityState = "loading" | "ready" | "notMapped" | "error";

export interface VenueBoardQuery {
    /** Resolve by id when the caller already has one (a tagged match, or a deep link). */
    venueId?: string | null;
    /** Otherwise resolve by proximity to the match's GPS centre point. */
    centre?: CentrePoint | null;
}

export interface VenueBoardResult {
    venue: VenuePublic | null;
    board: VenueBoardPublic | null;
    community: CommunityState;
    /** Refetch, bypassing the cache. */
    reload: () => Promise<void>;
}

export function useVenueBoard(query: VenueBoardQuery): VenueBoardResult {
    const { venueId, centre } = query;
    const [venue, setVenue] = useState<VenuePublic | null>(null);
    const [board, setBoard] = useState<VenueBoardPublic | null>(null);
    const [community, setCommunity] = useState<CommunityState>("loading");

    // A submission for one of my own matches drops that venue's keys and bumps this, which is
    // what makes an already-mounted stats tab refetch rather than keep the pre-match board.
    const storeVersion = useVenueStoreVersion();

    // Set once on unmount so a late response cannot write to a dead component.
    const unmountedRef = useRef(false);
    useEffect(
        () => () => {
            unmountedRef.current = true;
        },
        []
    );

    const latitude = centre?.latitude;
    const longitude = centre?.longitude;

    const run = useCallback(
        async (bypassCache: boolean) => {
            if (bypassCache) {
                clearVenueStore();
            }
            setCommunity("loading");

            try {
                const point = { latitude, longitude };
                let resolved: VenuePublic | null = null;

                if (venueId) {
                    resolved = await loadVenue(venueId);
                } else if (isUsableCentrePoint(point)) {
                    const venues = await loadVenuesNear(
                        latitude as number,
                        longitude as number
                    );
                    resolved = nearestVenue(venues);
                }

                if (unmountedRef.current) {
                    return;
                }
                setVenue(resolved);

                if (!resolved) {
                    setCommunity("notMapped");
                    return;
                }

                const snapshot = await loadBoard(resolved.venueId);
                if (unmountedRef.current) {
                    return;
                }
                setBoard(snapshot);
                setCommunity("ready");
            } catch (error) {
                if (unmountedRef.current) {
                    return;
                }
                // A 404 is not a failure — it means this venue has not been mapped yet, which is
                // the normal state early on. Anything else is a real error worth reporting.
                if (statusOfError(error) === 404) {
                    setCommunity("notMapped");
                    return;
                }
                Sentry.captureException(error);
                setCommunity("error");
            }
        },
        [venueId, latitude, longitude]
    );

    useEffect(() => {
        run(false);
        // `storeVersion` is a dependency rather than a value we read: an invalidation has to
        // re-run the load, and the store's own dedupe keeps an unrelated bump from costing a
        // round trip.
    }, [run, storeVersion]);

    const reload = useCallback(() => run(true), [run]);

    return { venue, board, community, reload };
}
